<template>
  <div class="bg-white rounded-lg py-4 px-1">
    <div class="flex items-center justify-between">
      <!-- Left side - Title and description -->
      <div class="flex-1">
        <div class="flex items-center gap-3 mb-2">
          <img src="/google-cal.png" alt="Google Calendar" class="w-6 h-6" />
          <h3 class="text-md font-semibold text-gray-900">Google Calendar Sync</h3>
        </div>
        <p class="text-xs text-gray-600">
          Authenticate Google Calendar with ElevatED to automatically create calendar events for
          your tutoring sessions.
        </p>
      </div>

      <!-- Right side - Action button -->
      <div class="ml-6">
        <button
          v-if="!isConnected"
          @click="connectGoogleCalendar"
          :disabled="isLoading"
          class="px-3 py-2 bg-white text-xs border-2 border-black shadow-[3px_3px_0px_0px_#000000] rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-3 cursor-pointer"
        >
          <svg
            v-if="isLoading"
            class="w-5 h-5 animate-spin text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <img v-else src="/google-cal.png" alt="Google Calendar" class="w-5 h-5" />
          {{ isLoading ? "Connecting..." : "Add Google Calendar" }}
        </button>

        <button
          v-else
          @click="disconnectGoogleCalendar"
          :disabled="isLoading"
          class="px-3 py-2 bg-white text-xs border-2 border-red-700 shadow-[3px_3px_0px_0px_#b91c1c] text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-3"
        >
          <svg
            v-if="isLoading"
            class="w-5 h-5 animate-spin"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          {{ isLoading ? "Disconnecting..." : "Disconnect" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { auth, onAuthStateChanged } from "@/firebase";
import {
  isGoogleCalendarConnected,
  removeGoogleCalendarConnection,
} from "@/services/googleCalendarApi";
import { logError, logSuccess } from "@/utils/secureLogger";

// Component state
const isLoading = ref(false);
const isConnected = ref(false);
const currentUser = ref<any>(null);

// Google OAuth configuration
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/oauth-callback`;

// Check if user is connected to Google Calendar
const checkConnectionStatus = async () => {
  if (!currentUser.value) return;

  try {
    isConnected.value = await isGoogleCalendarConnected(currentUser.value.uid);
  } catch (error) {
    logError("Error checking Google Calendar connection", error);
  }
};

// Connect to Google Calendar
const connectGoogleCalendar = () => {
  isLoading.value = true;

  // Build Google OAuth URL
  const googleAuthUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  googleAuthUrl.searchParams.set("client_id", GOOGLE_CLIENT_ID);
  googleAuthUrl.searchParams.set("redirect_uri", REDIRECT_URI);
  googleAuthUrl.searchParams.set("response_type", "code");
  googleAuthUrl.searchParams.set("scope", "https://www.googleapis.com/auth/calendar.events");
  googleAuthUrl.searchParams.set("access_type", "offline");
  googleAuthUrl.searchParams.set("prompt", "consent");

  // Redirect to Google OAuth
  window.location.href = googleAuthUrl.toString();
};

// Disconnect from Google Calendar
const disconnectGoogleCalendar = async () => {
  if (!currentUser.value) return;

  // Check if tutor has availability schedules
  try {
    const { db, collection, getDocs } = await import('@/firebase');
    const availabilityRef = collection(db, 'users', currentUser.value.uid, 'availability');
    const availabilitySnapshot = await getDocs(availabilityRef);
    
    if (!availabilitySnapshot.empty) {
      // Alert user they cannot disconnect with active availability
      const { default: Swal } = await import('sweetalert2');
      await Swal.fire({
        icon: 'error',
        title: 'Cannot Disconnect',
        html: `
          <p>You cannot disconnect Google Calendar while you have active availability schedules.</p>
          <p class="mt-2 text-sm text-gray-600">Please remove all your availability schedules first from your Tutor Profile page.</p>
        `,
        confirmButtonText: 'OK',
        confirmButtonColor: '#7c3aed',
      });
      return;
    }
  } catch (error) {
    logError('Error checking availability', error);
    // If we can't check, show error and don't allow disconnect
    const { default: Swal } = await import('sweetalert2');
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Unable to verify availability status. Please try again.',
      confirmButtonColor: '#7c3aed',
    });
    return;
  }

  isLoading.value = true;

  try {
    await removeGoogleCalendarConnection(currentUser.value.uid);
    isConnected.value = false;
    logSuccess("Google Calendar disconnected successfully");
  } catch (error) {
    logError("Error disconnecting Google Calendar", error);
  } finally {
    isLoading.value = false;
  }
};

// Check connection status on component mount
onMounted(() => {
  // Listen for auth state changes
  onAuthStateChanged(auth, (user) => {
    currentUser.value = user;
    if (user) {
      checkConnectionStatus();
    }
  });
});
</script>
